// Closet — TRIMIE Hi-Fi v1 (per docs/design-spec.md §4.8 + Hi-Fi PDF p.7)
//
// Header: title "Closet" + search & filter icons.
// Chip rail: "All · N" / Tops · N / Bottoms · N / Dresses · N / Outerwear · N.
// 3-column grid — FIRST tile is the dashed "+ ADD ITEM" tile.
// Heart toggles favorite (localStorage until backend has is_favorite).
// Inline rename + delete preserved (delete moved into edit row).

import { useState, useEffect, useRef } from 'react';
import api from './api';
import Header from './components/Header';
import EmptyState from './components/EmptyState';
import { CATEGORIES } from './AddClothingPage';
import { resolveUrl } from './utils/media';

const FAV_KEY = 'trimie_favorite_garments';

// Filter chips driven by the real `category` field. "All" + the fixed taxonomy
// from AddClothingPage (which mirrors the backend choices).
const CHIP_FILTERS = [
  { key: 'all', label: 'All' },
  ...CATEGORIES.map((c) => ({ key: c.value, label: c.label })),
];

const categoryLabel = (value) =>
  CATEGORIES.find((c) => c.value === value)?.label || 'Other';

const loadFavorites = () => {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveFavorites = (set) => {
  try { localStorage.setItem(FAV_KEY, JSON.stringify([...set])); }
  catch { /* quota — ignore */ }
};

const FilterIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5h18M6 12h12M10 19h4" />
  </svg>
);

function GarmentCard({ g, isFavorite, onToggleFavorite, onDelete, onUpdate, deleting }) {
  const [editing, setEditing] = useState(false);
  const [draftLabel, setDraftLabel] = useState(g.label || '');
  const [draftCategory, setDraftCategory] = useState(g.category || 'other');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraftLabel(g.label || '');
    setDraftCategory(g.category || 'other');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraftLabel(g.label || '');
    setDraftCategory(g.category || 'other');
  };

  const saveEdit = async () => {
    const trimmed = draftLabel.trim();
    const labelChanged = trimmed && trimmed !== g.label;
    const categoryChanged = draftCategory && draftCategory !== g.category;
    if (!labelChanged && !categoryChanged) { cancelEdit(); return; }
    setSaving(true);
    try {
      await onUpdate(g.id, {
        ...(labelChanged ? { label: trimmed } : {}),
        ...(categoryChanged ? { category: draftCategory } : {}),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const imgUrl = resolveUrl(g.input_url || g.image);

  return (
    <div className="garment-card">
      <div className="garment-img-wrap">
        {imgUrl
          ? <img src={imgUrl} alt={g.label || 'garment'} />
          : (
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--t-line)" strokeWidth="1.4">
              <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" />
            </svg>
          )}

        {/* Heart favorite */}
        <button
          className={`garment-heart${isFavorite ? ' active' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(g.id); }}
          aria-label={isFavorite ? 'Unfavorite' : 'Favorite'}
          type="button"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
          </svg>
        </button>
      </div>

      {editing ? (
        <div className="garment-edit-stack" onClick={(e) => e.stopPropagation()}>
          <div className="garment-edit-row">
            <input
              ref={inputRef}
              value={draftLabel}
              onChange={(e) => setDraftLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
              className="garment-edit-input"
              placeholder="Label"
            />
            <button onClick={saveEdit} disabled={saving} className="garment-edit-save" type="button">
              {saving ? '…' : '✓'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(g.id); }}
              disabled={deleting}
              className="garment-edit-del"
              aria-label="Delete garment"
              type="button"
            >
              {deleting
                ? <span className="spinner" style={{ width: 10, height: 10, borderWidth: 2 }} />
                : (
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
            </button>
          </div>
          <select
            value={draftCategory}
            onChange={(e) => setDraftCategory(e.target.value)}
            className="garment-edit-cat"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      ) : (
        <div className="garment-meta">
          <div className="garment-meta-text">
            <span className="garment-label">{g.label || 'Untitled'}</span>
            <span className="garment-cat-pill">{categoryLabel(g.category)}</span>
          </div>
          <button
            onClick={startEdit}
            className="garment-edit-btn"
            aria-label="Edit garment"
            type="button"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" strokeLinecap="round" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default function ClosetPage({ onNav }) {
  const [garments, setGarments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [activeChip, setActiveChip] = useState('all');
  const [favorites, setFavorites] = useState(loadFavorites);

  const fetchGarments = () => {
    setLoading(true);
    api.get('/tryon/upload-row-clothes/')
      .then(res => {
        const data = res.data?.data?.results ?? res.data?.data ?? [];
        setGarments(Array.isArray(data) ? data : []);
      })
      .catch(() => setGarments([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchGarments(); }, []);

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      saveFavorites(next);
      return next;
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this garment from your closet?')) return;
    setDeleting(id);
    try {
      await api.delete(`/tryon/upload-row-clothes/${id}/`);
      setGarments(prev => prev.filter(g => g.id !== id));
      if (favorites.has(id)) {
        const next = new Set(favorites); next.delete(id); saveFavorites(next); setFavorites(next);
      }
    } catch { /* already gone */ }
    finally { setDeleting(null); }
  };

  const handleUpdate = async (id, patch) => {
    const res = await api.patch(`/tryon/upload-row-clothes/${id}/`, patch);
    const updated = res.data?.data;
    if (updated) setGarments(prev => prev.map(g => g.id === id ? { ...g, ...updated } : g));
  };

  // Counts per chip — uses the real category field. "All" is the total.
  const counts = CHIP_FILTERS.reduce((acc, c) => {
    acc[c.key] = c.key === 'all'
      ? garments.length
      : garments.filter(g => (g.category || 'other') === c.key).length;
    return acc;
  }, {});

  const activeFilter = CHIP_FILTERS.find(c => c.key === activeChip) || CHIP_FILTERS[0];
  const visible = activeFilter.key === 'all'
    ? garments
    : garments.filter(g => (g.category || 'other') === activeFilter.key);

  return (
    <div className="screen">
      <Header
        title="Closet"
        action={{ icon: FilterIcon, ariaLabel: 'Filter closet' }}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <span className="spinner" style={{ color: 'var(--t-purple)', width: 28, height: 28, borderWidth: 3 }} />
        </div>
      ) : garments.length === 0 ? (
        <EmptyState
          iconShape="rounded"
          icon={
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--t-purple)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 8.5l7-4 7 4v9l-7 3-7-3v-9Z" />
              <path d="M5 8.5l7 3.5 7-3.5" />
              <path d="M12 12v8" />
            </svg>
          }
          title="Your closet is empty."
          body="Add a piece by snapping a photo or pasting a product link."
          actions={
            <>
              <button className="btn-purple" onClick={() => onNav('add')}>+ Add first item</button>
              <button className="btn-outline" onClick={() => onNav('add')}>Browse examples</button>
            </>
          }
        />
      ) : (
        <>
          {/* Chip filter rail with counts. Hides empty non-"All" chips. */}
          <div className="looks-chips" style={{ paddingTop: 10 }}>
            {CHIP_FILTERS.map((c) => {
              const n = counts[c.key];
              if (c.key !== 'all' && n === 0) return null;
              return (
                <button
                  key={c.key}
                  className={`looks-chip${activeChip === c.key ? ' active' : ''}`}
                  onClick={() => setActiveChip(c.key)}
                  type="button"
                >
                  {c.label} · {n}
                </button>
              );
            })}
          </div>

          <div className="closet-grid">
            {/* Add Item tile is always first */}
            <button
              className="closet-add-tile"
              onClick={() => onNav('add')}
              type="button"
              aria-label="Add item"
            >
              <div className="closet-add-plus">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </div>
              <span className="closet-add-label">ADD ITEM</span>
            </button>

            {visible.map(g => (
              <GarmentCard
                key={g.id}
                g={g}
                isFavorite={favorites.has(g.id)}
                onToggleFavorite={toggleFavorite}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
                deleting={deleting === g.id}
              />
            ))}
          </div>

          {visible.length === 0 && (
            <div style={{ padding: '24px 20px 0', textAlign: 'center', color: 'var(--t-muted)', fontSize: 13 }}>
              Nothing in {activeFilter.label.toLowerCase()} yet.
            </div>
          )}
        </>
      )}
    </div>
  );
}
